// @flow

import * as R from 'ramda';

import { TokenService, HttpService, AceService } from '~/services';
import { LOGGED_IN_USER, VIEWER } from '~/gql';
import { parseBool, renameKeys, mergeDeepRightAll } from '~/helpers';

const checkExpiredToken = (value: ?string) =>
  TokenService.isTokenExpired(value) ? null : value;

let previousRequestCancelToken;

export const resolvers = {
  Query: {
    loggedInUser: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      // $FlowIssue
      __, // eslint-disable-line no-unused-vars
      { cache }: ApolloCache,
    ): Promise<?LoggedInUser> => {
      const authTokens = R.pipe(
        R.map(checkExpiredToken),
        R.reject((value) => !value),
      )((await AceService.getAuthTokens()) || {});

      const loggedInUser = !R.isEmpty(authTokens)
        ? {
            ...authTokens,
            id: TokenService.getUserId(authTokens.idToken),
            isDepositUser: TokenService.isDepositUser(authTokens.idToken),
            __typename: 'LoggedInUser',
          }
        : null;

      if (loggedInUser) {
        let oldLoggedInUser = {};

        try {
          const cachedLoggedInUser: LoggedInUserQuery = cache.readQuery({
            query: LOGGED_IN_USER,
            variables: { includeIdToken: false },
          });

          oldLoggedInUser = R.prop('loggedInUser', cachedLoggedInUser);
        } catch (error) {}

        if (
          R.prop('accessToken', oldLoggedInUser) !==
          R.prop('accessToken', loggedInUser)
        ) {
          if (previousRequestCancelToken) {
            previousRequestCancelToken();
          }

          await HttpService.client.post(
            '/Account/Login',
            {},
            {
              cancelToken: new HttpService.CancelToken((cancelToken) => {
                previousRequestCancelToken = cancelToken;
              }),
              headers: {
                platform_type: process.env.REACT_APP_TYPE,
              },
            },
          );
        }
      }

      cache.writeData({ data: { loggedInUser } });

      return loggedInUser;
    },
    viewer: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      // $FlowIssue
      __, // eslint-disable-line no-unused-vars
      { cache }: ApolloCache,
    ): Promise<?Viewer> => {
      // Get user details
      const {
        data: {
          MyProfile: {
            AgentAgreementPopUp,
            AgentEligible,
            AgentStatus,
            CumulativePersonalRewards,
            HasDepositBalance,
            PendingPersonalRewards,
            WithdrawalLimit,
            WithdrawalLimitGlobal,
            WithdrawalLimitVIP,
            MissionReward: {
              LastMonthCommissionRate,
              LastMonthTotalAmtInUsd,
              CurrMonthCommissionRate,
              CurrMonthAccTotalAmtInUsd,
              GoalCommissionRate,
              GoalAmtInUsd,
            },
            ...rest
          },
        },
      } = await HttpService.client.get('/Profile/GetMyProfile');

      const viewer: Viewer = {
        ...mergeDeepRightAll(
          renameKeys({
            UserGuid: 'id',
            Username: 'passportNumber',
            ReferralGuid: 'referralId',
            ReferralName: 'referralPassportNumber',
            DepositBtcAddress: 'depositBTCAddress',
            WithdrawalBtcAddress: 'withdrawalBTCAddress',
            UserRole: 'role',
            ArchivedVersion: 'archivedVersions',
            PersonalRewardsOnPlatform: 'personalRewardsOnPlatform',
          })(rest),
          {
            isAgent: parseBool(AgentStatus),
            isAgentEligible: parseBool(AgentEligible),
            isAgreementPopupAllowed: parseBool(AgentAgreementPopUp),
            hasDeposit: parseBool(HasDepositBalance),
            withdrawalLimits: {
              self: parseFloat(WithdrawalLimit),
              vip: parseFloat(WithdrawalLimitVIP),
              total: parseFloat(WithdrawalLimitGlobal),
              __typename: 'WithdrawalBalancesLimitsValues',
            },
            missionReward: {
              lastMonthCommissionRate: LastMonthCommissionRate,
              lastMonthTotalAmtInUsd: LastMonthTotalAmtInUsd,
              currMonthCommissionRate: CurrMonthCommissionRate,
              currMonthAccTotalAmtInUsd: CurrMonthAccTotalAmtInUsd,
              goalCommissionRate: GoalCommissionRate,
              goalAmtInUsd: GoalAmtInUsd,
              __typename: 'WithdrawalMissionRewardValues',
            },
            __typename: 'Viewer',
          },
        ),
      };

      cache.writeData({ data: { viewer } });

      return viewer;
    },
    myWallet: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      props: MyWalletInputProps,
      { cache }: ApolloCache,
    ): Promise<?MyWallet> => {
      const locale = R.propOr('en', 'locale', props);
      const isEnLocale = locale === 'en';

      const {
        data: {
          MyWealth: {
            CityCredit,
            CityScore,
            CityScorePercentage,
            CityScorePercentage2,
            NextCityScore,
            NextCityScorePercentage,
            NextCityScorePercentage2,
            SelfCityScore,
            DepositBalance,
            AvailableBalance,
            TotalDepositAmount,
            BalanceWithdrawalLimit,
            BalanceWithdrawalLimitVIP,
            BalanceWithdrawalGlobal,
            WithdrawalLimit,
            WithdrawalLimitVIP,
            WithdrawalLimitGlobal,
            GrowthRewardPercentage,
            LockedEarningAmount,
            SopIncome,
            TotalDepositReturnedAmount,
            TotalMegopolyAmount,
            TotalRewardEarned,
            VaultCapacity,
            VaultExcessAmount,
            IsLoanEnabled,
            IsDepositEnabled,
            IsWithdrawalEnabled,
            IsDepositAllowEqualPctOffset,
          },
        },
      } = await HttpService.client.get('/MyWealth/GetMyWealth');

      const myWallet: MyWallet = {
        cityCredit: parseFloat(CityCredit),
        city: {
          scoreValue: parseFloat(CityScore),
          scorePtc: parseFloat(
            isEnLocale ? CityScorePercentage : CityScorePercentage2,
          ),
          nextScoreValue: parseFloat(NextCityScore),
          nextScorePtc: parseFloat(
            isEnLocale ? NextCityScorePercentage : NextCityScorePercentage2,
          ),
          __typename: 'ScoreValues',
        },
        selfCityScore: parseFloat(SelfCityScore),
        deposit: {
          scoreValue: parseFloat(DepositBalance),
          scorePtc: Math.ceil((DepositBalance / TotalDepositAmount) * 100) || 0,
          nextScoreValue: parseFloat(TotalDepositAmount),
          nextScorePtc: null,
          __typename: 'ScoreValues',
        },
        withdrawalBalances: {
          self: parseFloat(BalanceWithdrawalLimit),
          vip: parseFloat(BalanceWithdrawalLimitVIP),
          total: parseFloat(BalanceWithdrawalGlobal),
          __typename: 'WithdrawalBalancesLimitsValues',
        },
        withdrawalLimits: {
          self: parseFloat(WithdrawalLimit),
          vip: parseFloat(WithdrawalLimitVIP),
          total: parseFloat(WithdrawalLimitGlobal),
          __typename: 'WithdrawalBalancesLimitsValues',
        },
        balance: parseFloat(AvailableBalance),
        growthReward: parseFloat(GrowthRewardPercentage),
        lockedEarning: parseFloat(LockedEarningAmount),
        sopIncome: parseFloat(SopIncome),
        totalMegopoly: parseFloat(TotalMegopolyAmount),
        totalReturned: parseFloat(TotalDepositReturnedAmount),
        totalReward: parseFloat(TotalRewardEarned),
        vaultCapacity: parseFloat(VaultCapacity),
        vaultExcess: parseFloat(VaultExcessAmount),
        isLoanEnabled: parseBool(IsLoanEnabled),
        isDepositEnabled: parseBool(IsDepositEnabled),
        isWithdrawalEnabled: parseBool(IsWithdrawalEnabled),
        allowEqualPctOffset: parseBool(IsDepositAllowEqualPctOffset),
        __typename: 'MyWallet',
      };

      cache.writeData({ data: { myWallet }, variables: props });

      return myWallet;
    },
  },
  Mutation: {
    viewerMutation: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      { values: { withdrawalBTCAddress } = {} }: ViewerInputMutation,
      { cache }: ApolloCache,
    ): Promise<MessagePayload> => {
      // Update user
      const {
        data: { BtcAddress },
      } = await HttpService.client.post(
        '/Profile/ChangeBtcAddressApplication',
        { BtcAddress: withdrawalBTCAddress },
      );

      const { viewer }: ViewerQuery = cache.readQuery({
        query: VIEWER,
      });

      // Update cache
      cache.writeData({
        data: { viewer: { ...viewer, withdrawalBTCAddress: BtcAddress } },
      });

      return {
        code: 0,
        message: 'Done',
        __typename: 'MessagePayload',
      };
    },
    updateToAgentRoleMutation: async (): Promise<MessagePayload> => {
      try {
        // Update profile role to agent
        const {
          data: { ReturnCode = 0 },
        } = await HttpService.client.post('/Profile/UpdateToAgentRole');

        return {
          code: ReturnCode,
          message: 'Done',
          __typename: 'MessagePayload',
        };
      } catch (error) {
        return {
          code: 0,
          message: error.message,
          __typename: 'MessagePayload',
        };
      }
    },
    updateAgentAgreementPopUpMutation: async (): Promise<MessagePayload> => {
      // Update agent agreement popup visibility
      await HttpService.client.post('/Profile/UpdateAgentAgreementPopUp');

      return {
        code: 0,
        message: 'Done',
        __typename: 'MessagePayload',
      };
    },
    setPersonalRewardPlatformTransferMutation: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      { platform }: PlatformType,
      { cache }: ApolloCache,
    ): Promise<MessagePayload> => {
      try {
        const {
          data: { ReturnCode },
        } = await HttpService.client.post(
          '/Profile/PersonalRewardPlatformTransfer',
          { Platform: platform },
        );

        const { viewer } = cache.readQuery({
          query: VIEWER,
        });

        cache.writeData({
          query: VIEWER,
          data: {
            viewer: { ...viewer, ...{ personalRewardsOnPlatform: platform } },
          },
        });

        return {
          code: ReturnCode,
          message: 'Done',
          __typename: 'MessagePayload',
        };
      } catch (e) {
        return {
          code: -1,
          message: 'Error',
          __typename: 'MessagePayload',
        };
      }
    },
    setDepositAllowEqualPctOffset: async (
      _, // eslint-disable-line no-unused-vars
      { value },
    ) => {
      try {
        await HttpService.client.post('/Profile/DepositAllowEqualPctOffset', {
          AllowEqualPctOffset: value,
        });

        return {
          code: 0,
          message: 'Done',
          __typename: 'MessagePayload',
        };
      } catch (e) {
        return {
          code: -1,
          message: 'Error',
          __typename: 'MessagePayload',
        };
      }
    },
  },
};
